import {
    EventInfoWithPlayersQuery,
    EventsInfoWithFilteredPlayersQuery,
    LeagueUpcomingEventsQuery,
} from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { GetLobbyContentQuery, GetUserFavoritesQuery } from '@/api/lobby/query.generated';
import { PicksPlayerPropsStoreUpdater } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { logger } from '@/utils/logging';
import { Exchange, getOperationName } from 'urql';
import { pipe, tap } from 'wonka';

const LOG_TAG = '[GraphQL Interceptor]';

/**
 * Intercepts operations on their way to the network and the results coming back.
 *
 * Must be placed below the `cacheExchange` so it only sees cache misses and fresh
 * network/subscription results, instead of firing on every cache hit.
 *
 * It is the place where the player-props store update will move to,
 * so the store is written once per API response instead of once per render (PIC-9155).
 */
export const requestInterceptorExchange: Exchange =
    ({ forward }) =>
    operations$ =>
        pipe(
            operations$,
            forward,
            tap(result => {
                if (!result.data) {
                    logger.debug(LOG_TAG, 'result has no data');
                    return;
                }
                const operationName = getOperationName(result.operation.query) ?? 'unknown';
                if (operationName === 'LeagueUpcomingEvents') {
                    const data = result.data as LeagueUpcomingEventsQuery;
                    PicksPlayerPropsStoreUpdater.updatePlayerStoreWithNewData(data.getUpcomingEventsV2, {
                        debugTag: 'interceptor',
                    });
                } else if (operationName === 'GetLobbyContent') {
                    const data = result.data as GetLobbyContentQuery;
                    const events = mergeEvents(data?.getTopTrendingPlayersData, data?.getUpcomingLobbyEventsV2);
                    PicksPlayerPropsStoreUpdater.updatePlayerStoreWithNewData(events, { debugTag: 'interceptor' });
                } else if (operationName === 'EventsInfoWithFilteredPlayers') {
                    const data = result.data as EventsInfoWithFilteredPlayersQuery;
                    PicksPlayerPropsStoreUpdater.updatePlayerStoreWithNewData(data.getEventsWithFilteredPlayers, {
                        debugTag: 'interceptor',
                    });
                } else if (operationName === 'EventInfoWithPlayers') {
                    const data = result.data as EventInfoWithPlayersQuery;
                    PicksPlayerPropsStoreUpdater.updatePlayerStoreWithNewData(
                        data.getEventByIdV2 ? [data.getEventByIdV2] : [],
                        {
                            debugTag: 'interceptor',
                        }
                    );
                } else if (operationName === 'GetUserFavorites') {
                    const data = result.data as GetUserFavoritesQuery;
                    PicksPlayerPropsStoreUpdater.updatePlayerStoreWithNewData(data.getUpcomingFavoritePlayerEventsV2, {
                        debugTag: 'interceptor',
                    });
                }
            })
        );

const mergeEvents = (...events: (EventInfo[] | undefined)[]): EventInfo[] => {
    return [...events].filter(Boolean).flat();
};
